import React, { useState, useEffect, useRef, useContext } from 'react';
import { API_URL } from '../config/constants';
import { AuthContext } from '../context/AuthContext';
import Drawer from './Drawer';
import CreateAccessory from '../pages/CreateAccessory'; // Ortak Button bileşenini import et
import Button from './ui/Button';
import Modal from './Modal'; // Add this import
import { Select, Input, notification } from 'antd';

interface Accessory {
  _id: string;
  name: string;
  quantity: string | number;
  description?: string;
  images?: string;
  code?: string;
  type?: string;
  color?: string;
  unit: string;
}

interface AccessorySelectorProps {
  selectedAccessories: Accessory[];
  onAccessorySelect: (accessory: Accessory) => void;
  onAccessoryRemove: (accessoryId: string) => void;
}

const AccessorySelector: React.FC<AccessorySelectorProps> = ({
  selectedAccessories,
  onAccessorySelect,
  onAccessoryRemove
}) => {
  const { hasPermission } = useContext(AuthContext);
  const [isOpen, setIsOpen] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [accessories, setAccessories] = useState<Accessory[]>([]);
  const [filteredAccessories, setFilteredAccessories] = useState<Accessory[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const selectorRef = useRef<HTMLDivElement>(null);
  const [editingAccessory, setEditingAccessory] = useState<{
    accessory: Accessory;
    isNew: boolean;
  } | null>(null);
  const [quantityForm, setQuantityForm] = useState<{
    quantity: string | number;
    notes: string;
    rawInput: string;
  }>({
    quantity: '',
    notes: '',
    rawInput: ''
  });
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    onConfirm: () => {}
  });
  const [newAccessoryModal, setNewAccessoryModal] = useState<{
    isOpen: boolean;
    accessory: Accessory | null;
  }>({
    isOpen: false,
    accessory: null
  });
  const [selectedAccessory, setSelectedAccessory] = useState<string | null>(null);
  const [quantity, setQuantity] = useState<string>('');

  // Aksesuarları yükle
  useEffect(() => {
    const fetchAccessories = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await fetch(`${API_URL}/accessories`);
        if (!response.ok) {
          throw new Error('Aksesuarlar getirilemedi');
        }
        const accessoriesList = await response.json();
        setAccessories(accessoriesList);
        setFilteredAccessories(accessoriesList);
      } catch (error) {
        setError('Aksesuarlar yüklenirken bir hata oluştu');
        notification.error({
          message: 'Hata',
          description: 'Aksesuarlar yüklenirken bir hata oluştu.'
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchAccessories();
  }, []);

  // Seçili aksesuarları izle
  useEffect(() => {
    console.log('Seçili aksesuarlar güncellendi:', selectedAccessories);
    // Seçili aksesuarlar değiştiğinde filtreleme yap
    if (selectedAccessories.length > 0) {
      const filtered = accessories.filter(acc => 
        !selectedAccessories.some(selected => selected._id === acc._id)
      );
      setFilteredAccessories(filtered);
    } else {
      setFilteredAccessories(accessories);
    }
  }, [selectedAccessories, accessories]);

  // Arama filtresini uygula
  useEffect(() => {
    const filtered = accessories.filter(accessory =>
      accessory.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredAccessories(filtered);
  }, [searchTerm, accessories]);

  // Modal içindeki miktar input değişikliği
  const handleQuantityInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setQuantityForm(prev => ({
      ...prev,
      rawInput: newValue,
      quantity: newValue
    }));
  };

  // Form submit
  const handleEditSubmit = () => {
    if (!editingAccessory) return;

    if (!quantityForm.rawInput) {
      notification.warning({
        message: 'Uyarı',
        description: 'Lütfen miktar girin'
      });
      return;
    }

    const updatedAccessory = {
      ...editingAccessory.accessory,
      quantity: quantityForm.rawInput,
      description: quantityForm.notes
    };

    console.log('Aksesuar güncelleniyor:', updatedAccessory);
    onAccessorySelect(updatedAccessory);
    setEditingAccessory(null);
    setQuantityForm({ quantity: '', notes: '', rawInput: '' });
  };

  // Aksesuar düzenleme
  const handleEditAccessory = (accessory: Accessory, isNew: boolean) => {
    const existingAccessory = selectedAccessories.find(a => a._id === accessory._id);
    console.log('Düzenleme başlatıldı:', { accessory, existingAccessory });

    setQuantityForm({
      quantity: existingAccessory ? existingAccessory.quantity : '',
      notes: existingAccessory?.description || '',
      rawInput: existingAccessory ? existingAccessory.quantity.toString() : ''
    });
    
    setEditingAccessory({ accessory, isNew });
    setIsOpen(false);
  };

  // Yeni aksesuar başarılı
  const handleNewAccessorySuccess = (accessory: Accessory) => {
    setIsDrawerOpen(false);
    setNewAccessoryModal({
      isOpen: true,
      accessory
    });
  };

  // Yeni aksesuar miktar submit
  const handleNewAccessoryQuantitySubmit = (quantity: string) => {
    if (newAccessoryModal.accessory) {
      const accessoryWithQuantity = {
        ...newAccessoryModal.accessory,
        quantity
      };
      onAccessorySelect(accessoryWithQuantity);
      setNewAccessoryModal({ isOpen: false, accessory: null });
    }
  };

  // Yükleme durumu
  if (isLoading) {
    return (
      <div className="flex justify-center items-center p-4">
        <div className="loader ease-linear rounded-full border-4 border-t-4 border-gray-200 h-8 w-8 border-t-purple-600 animate-spin"></div>
        <span className="ml-2 text-gray-500">Aksesuarlar yükleniyor...</span>
      </div>
    );
  }

  // Hata durumu
  if (error) {
    return (
      <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 rounded">
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div className="relative" ref={selectorRef}>
      {/* Seçici Butonu */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-white text-left flex justify-between items-center"
      >
        <span className="text-gray-700">
          {selectedAccessories.length > 0 
            ? `${selectedAccessories.length} aksesuar seçildi`
            : 'Aksesuar Seçin'}
        </span>
        <svg className={`w-5 h-5 text-gray-400 ${isOpen ? 'transform rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg">
          <div className="p-2 border-b sticky top-0 bg-white z-10">
            <div className="relative">
              <input
                type="text"
                placeholder="Aksesuar Ara..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-8 py-2 border border-gray-300 rounded-lg"
              />
              <svg className="absolute left-2 top-2.5 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </div>
          <div className="max-h-[400px] overflow-y-auto p-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {filteredAccessories.map(accessory => (
                <button
                  key={accessory._id}
                  onClick={() => handleEditAccessory(accessory, true)}
                  className="w-full text-left hover:bg-gray-50 p-2 rounded-lg border border-gray-200 transition-colors"
                >
                  <div className="flex gap-3">
                    <div className="w-16 h-16 rounded-md overflow-hidden bg-gray-100 flex-shrink-0">
                      {accessory.images && Array.isArray(accessory.images) && accessory.images[0] ? (
                        <img
                          src={API_URL + accessory.images}
                          alt={accessory.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-gray-400">
                          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-gray-900 truncate">{accessory.name}</div>
                      <div className="text-sm text-gray-500">Kod: {accessory.code || 'N/A'}</div>
                      <div className="text-sm text-gray-500 truncate">{accessory.type}</div>
                      {accessory.color && (
                        <div className="flex items-center gap-1.5 mt-1">
                          <div
                            className="w-3 h-3 rounded-full border border-gray-300"
                            style={{ backgroundColor: accessory.color }}
                          />
                          <span className="text-xs text-gray-500">{accessory.color}</span>
                        </div> 
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
          <div className="p-2 border-t sticky bottom-0 bg-white">
            {hasPermission('accessories', 'create') && (
              <button
                type="button"
                onClick={() => {
                  setIsOpen(false);
                  setIsDrawerOpen(true);
                }}
                className="w-full px-4 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700"
              >
                + Yeni Aksesuar Ekle
              </button>
            )}
          </div>
        </div>
      )}

      {/* Seçili Aksesuarlar Listesi */}
      <div className="mt-4">
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="p-4 border-b border-gray-200 bg-gray-50">
            <div className="flex items-center justify-between">
              <h3 className="font-medium text-gray-700">
                Seçili Aksesuarlar ({selectedAccessories.length})
              </h3>
              {selectedAccessories.length > 0 && (
                <button
                  type="button"
                  onClick={() => {
                    setConfirmModal({
                      isOpen: true,
                      onConfirm: () => {
                        selectedAccessories.forEach(acc => onAccessoryRemove(acc._id));
                        setConfirmModal({ isOpen: false, onConfirm: () => {} });
                      }
                    });
                  }}
                  className="text-sm text-red-600 hover:text-red-800"
                >
                  Tümünü Temizle
                </button>
              )}
            </div>
          </div>
          {selectedAccessories.length === 0 ? (
            <div className="p-8 text-center">
              <div className="mx-auto h-12 w-12 text-gray-400 mb-4">
                <svg className="h-full w-full" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
              </div>
              <p className="text-gray-500 text-sm">Henüz aksesuar seçilmedi</p>
              <p className="text-gray-400 text-xs mt-1">Aksesuar eklemek için yukarıdaki menüyü kullanın</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {selectedAccessories.map((accessory) => (
                <div key={accessory._id} className="p-4 hover:bg-gray-50 transition-colors">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0 mr-4">
                      <div className="flex items-center mb-1">
                        <h4 className="text-sm font-medium text-gray-900 truncate">{accessory.name}</h4>
                        <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                          {accessory.quantity} {accessory.unit}
                        </span>
                      </div>
                      {accessory.description && (
                        <p className="text-sm text-gray-500 line-clamp-2">{accessory.description}</p>
                      )}
                    </div>
                    <div className="flex items-start space-x-2">
                      <button
                        type="button"
                        onClick={() => handleEditAccessory(accessory, false)}
                        className="p-1 text-gray-400 hover:text-gray-600"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                        </svg>
                      </button>
                      <button
                        type="button"
                        onClick={() => onAccessoryRemove(accessory._id)}
                        className="p-1 text-gray-400 hover:text-red-600"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Miktar Modal */}
      {editingAccessory && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-medium mb-4">
              {editingAccessory.isNew ? 'Aksesuar Ekle' : 'Aksesuarı Düzenle'} - {editingAccessory.accessory.name}
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Miktar
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={quantityForm.rawInput}
                    onChange={handleQuantityInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    placeholder="Miktar giriniz"
                  />
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  Mevcut miktar: {quantityForm.quantity}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Not (Opsiyonel)
                </label>
                <textarea
                  value={quantityForm.notes}
                  onChange={(e) => setQuantityForm(prev => ({
                    ...prev,
                    notes: e.target.value
                  }))}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  placeholder="Aksesuar için özel notlar..."
                />
              </div>
            </div>
            <div className="mt-6 flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => setEditingAccessory(null)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
              >
                İptal
              </button>
              <button
                type="button"
                onClick={handleEditSubmit}
                className="px-4 py-2 text-white bg-blue-600 rounded-md hover:bg-blue-700"
              >
                {editingAccessory.isNew ? 'Ekle' : 'Güncelle'}
              </button>
            </div>
          </div>
        </div>
      )}

      <Drawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        title="Yeni Aksesuar Ekle"
      >
        <CreateAccessory
          onSuccess={handleNewAccessorySuccess}
          onCancel={() => setIsDrawerOpen(false)}
          isDrawerMode={true}
        />
      </Drawer>

      {/* Yeni Aksesuar Miktar Modalı */}
      {newAccessoryModal.isOpen && newAccessoryModal.accessory && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-medium mb-4">
              Aksesuar Miktarı - {newAccessoryModal.accessory.name}
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Miktar
                </label>
                <input
                  type="text"
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  placeholder="Miktar giriniz"
                  autoFocus
                />
              </div>
            </div>
            <div className="mt-6 flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => setNewAccessoryModal({ isOpen: false, accessory: null })}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
              >
                İptal
              </button>
              <button
                type="button"
                onClick={() => handleNewAccessoryQuantitySubmit(quantity)}
                className="px-4 py-2 text-white bg-blue-600 rounded-md hover:bg-blue-700"
              >
                Ekle
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Onay Modalı */}
      <Modal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ isOpen: false, onConfirm: () => {} })}
        onConfirm={confirmModal.onConfirm}
        title="Aksesuarları Temizle"
        type="warning"
        confirmText="Evet, Temizle"
        cancelText="İptal"
      >
        <p>Tüm seçili aksesuarları kaldırmak istediğinize emin misiniz?</p>
      </Modal>
    </div>
  );
};

export default AccessorySelector;
